import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private connectionPromise: Promise<Socket> | null = null;
  private reconnectCallbacks: Set<() => void> = new Set();

  async connect(token: string): Promise<Socket> {
    if (this.socket?.connected) {
      return this.socket;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
      
      this.socket = io(API_URL, {
        auth: { token },
        transports: ['polling', 'websocket'],
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });

      this.socket.on('connect', () => {
        console.log('Socket connected successfully:', this.socket?.id);
        resolve(this.socket!);
      });

      this.socket.on('connect_error', (err) => {
        console.error('Socket connection error:', err);
        reject(err);
      });

      this.socket.on('disconnect', () => {
        console.log('Socket disconnected');
      });

      this.socket.on('reconnect', (attemptNumber) => {
        console.log(`Socket reconnected after ${attemptNumber} attempts`);
        this.reconnectCallbacks.forEach(cb => cb());
      });

      this.socket.on('force_logout', () => {
        console.log('Force logout received from server');
        this.socket?.disconnect();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      });
    });

    return this.connectionPromise;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionPromise = null;
      this.listeners.clear();
      this.reconnectCallbacks.clear();
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit event:', event);
    }
  }

  on(event: string, callback: Function): void {
    if (!this.socket) {
      console.warn('Socket not initialized, cannot add listener for event:', event);
      return;
    }

    // Add callback to listeners map for tracking
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Add socket listener
    this.socket.on(event, callback as any);
    
    // Debug logging for message status events
    if (event === 'status_mensagem') {
      console.log('SocketService: Added listener for status_mensagem event');
    }
  }

  off(event: string, callback?: Function): void {
    if (!this.socket) return;

    if (callback) {
      // Remove specific callback
      this.socket.off(event, callback as any);
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
        if (eventListeners.size === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      // Remove all listeners for event
      this.socket.off(event);
      this.listeners.delete(event);
    }
  }

  joinRoom(room: string): void {
    this.emit('join_room', room);
  }

  leaveRoom(room: string): void {
    this.emit('leave_room', room);
  }

  joinEmpresa(empresaId: string): void {
    this.emit('join_empresa', empresaId);
  }

  joinConversa(conversaId: string): void {
    this.emit('join_conversa', conversaId);
  }

  leaveConversa(conversaId: string): void {
    this.emit('leave_conversa', conversaId);
  }

  onReconnect(callback: () => void): void {
    this.reconnectCallbacks.add(callback);
  }

  offReconnect(callback: () => void): void {
    this.reconnectCallbacks.delete(callback);
  }

  // Get all active listeners for debugging
  getActiveListeners(): string[] {
    return Array.from(this.listeners.keys());
  }

  // Get listener count for specific event
  getListenerCount(event: string): number {
    return this.listeners.get(event)?.size || 0;
  }
}

export const socketService = new SocketService();
export default socketService;
